/* ==========================================================================
   The mascot — a figure, a glow behind it and a shadow under it.
   Everything that moves moves `transform` or `opacity`, like the wheel, so a
   mascot never costs a layout or a paint while it plays.
   ========================================================================== */

.root {
  position: relative;
  display: inline-block;
  flex-shrink: 0;
  pointer-events: none;
  user-select: none;
}

/* A soft disc of the creator's colour, so the figure reads as sitting in her
   light rather than floating on the card. */
.halo {
  position: absolute;
  inset: 8% 4% 2%;
  border-radius: 50%;
  background: radial-gradient(closest-side, var(--brand-glow), transparent 72%);
  opacity: 0.85;
}

.figure {
  position: absolute;
  inset: 0;
  /* Flex so every pose — and the placeholder — stands on the same bottom line,
     centred horizontally, whatever its own width. The pose layers are absolute
     (they have to stack to crossfade), so they are grounded by `bottom: 0`
     directly; this only governs the in-flow placeholder. */
  display: flex;
  align-items: flex-end;
  justify-content: center;
  transform-origin: 50% 100%;
  will-change: transform;
  /* Keep the figure on its own compositor layer, rasterised at the device's
     pixel ratio, so a scaling motion resamples a crisp high-DPI texture rather
     than a 1x one blown up — the difference between sharp and soft on retina. */
  backface-visibility: hidden;
  transform: translateZ(0);
}

.art {
  display: block;
  /* Size every pose by its HEIGHT, not by fitting the whole frame into the box.
     The poses are uploaded as separate images and do NOT share one canvas (the
     server stores whatever bytes it is given, at any dimensions), so
     `object-fit: contain` fitted each one to its own aspect ratio — a taller
     "arm raised" frame was scaled down more than a compact "hands behind back"
     one, and the mascot visibly shrank when it swapped pose mid-spin. Pinning
     the rendered height instead (width follows the art; the extra transparent
     margins are invisible) keeps the figure the same height across every pose,
     and the parent's `align-items: flex-end` stands them all on one ground line
     under the shadow. NOTE: pixel-identical sizing still needs the source art
     exported on one shared square canvas with the figure at a constant scale —
     CSS cannot infer where the figure sits inside an arbitrary crop. */
  height: 100%;
  width: auto;
  max-width: 100%;
  /* High-quality resampling for the (always downscaled) portrait art. */
  image-rendering: auto;
  /* No `filter` here on purpose. A drop-shadow is a paint, and this element's
     parent animates `transform` every frame — the two together re-rasterise the
     portrait on every tick and crawled the animation to a few fps. The grounding
     shadow is the separate `.shadow` oval, which never has to repaint. */
}

/* Crossfading pose layers (see Mascot.tsx): stacked, each fades in over the one
   below, which stays opaque until it is pruned — a true dissolve between poses. */
.poseLayer {
  position: absolute;
  bottom: 0;
  left: 50%;
  /* Bottom-centred rather than stretched to `inset: 0`, so a height-sized pose
     (see `.art`) keeps its own width and the crossfading layers line up on the
     same ground line. */
  transform: translateX(-50%);
  animation: poseFade 0.44s cubic-bezier(0.22, 0.61, 0.36, 1) both;
}

/* The blink sits above the pose and swaps instantly — no fade, or a wink would
   dissolve instead of snapping shut. It shares the idle pose's dimensions, so
   the same bottom-centred anchor lands it exactly over the face. */
.blinkLayer {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
}

@keyframes poseFade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.shadow {
  position: absolute;
  left: 20%;
  right: 20%;
  bottom: 1%;
  height: 8%;
  border-radius: 50%;
  /* The soft edge comes from a radial gradient, NOT a `blur()` filter. The old
     `filter: blur(3px)` was animated every frame by `bobShadow`/`hopShadow`
     while this element was never promoted to its own compositor layer — so the
     blur was re-rasterised on the main thread on every tick, which is exactly
     the few-fps crawl the figure's own note warns against. A gradient costs no
     filter pass, and promoting the layer (below) keeps the transform/opacity
     animation on the GPU, off the main thread where the wheel's GSAP tween runs
     during a spin. */
  background: radial-gradient(closest-side, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0) 78%);
  transform-origin: 50% 50%;
  will-change: transform, opacity;
  transform: translateZ(0);
}

.avatar .figure {
  /* No halo or shadow to sit on, so the figure fills its box. */
  inset: 0;
}

/* --- Placeholder drawing --------------------------------------------------- */

.stroke {
  fill: none;
  stroke: var(--mascot-ink);
  stroke-width: 5;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.arm {
  fill: none;
  stroke: var(--mascot-skin);
  stroke-width: 14;
  stroke-linecap: round;
}

.tear {
  fill: none;
  stroke: var(--accent-cyan);
  stroke-width: 5;
  stroke-linecap: round;
}

/* The idle blink for the drawing: the eyes squash shut for a beat. Real art
   blinks by swapping to its `blink` image instead (see Mascot.tsx). */
.eyes {
  transform-box: fill-box;
  transform-origin: 50% 60%;
}

[data-state='idle'] .eyes {
  animation: blink 5.2s infinite;
}

/* --- Motions, one per `motion` in states.ts --------------------------------- */

[data-motion='bob'] .figure {
  animation: bob 3.8s cubic-bezier(0.37, 0, 0.63, 1) infinite;
}
[data-motion='bob'] .shadow {
  animation: bobShadow 3.8s cubic-bezier(0.37, 0, 0.63, 1) infinite;
}

[data-motion='jitter'] .figure {
  animation: jitter 0.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

[data-motion='hop'] .figure {
  animation: hop 0.8s cubic-bezier(0.3, 0.7, 0.4, 1) infinite;
}
[data-motion='hop'] .shadow {
  animation: hopShadow 0.8s cubic-bezier(0.3, 0.7, 0.4, 1) infinite;
}

[data-motion='hopBig'] .figure {
  animation: hopBig 0.7s cubic-bezier(0.3, 0.7, 0.4, 1) infinite;
}
[data-motion='hopBig'] .shadow {
  animation: hopShadow 0.7s cubic-bezier(0.3, 0.7, 0.4, 1) infinite;
}

[data-motion='droop'] .figure {
  animation: droop 2.4s ease-in-out infinite;
}

@keyframes bob {
  0%,
  100% {
    transform: translateY(0) scale(1, 1);
  }
  50% {
    transform: translateY(-3%) scale(1.015, 0.99);
  }
}
@keyframes bobShadow {
  0%,
  100% {
    transform: scaleX(1);
    opacity: 1;
  }
  50% {
    transform: scaleX(0.92);
    opacity: 0.8;
  }
}
@keyframes jitter {
  0%,
  100% {
    transform: rotate(-2.5deg) translateY(0);
  }
  50% {
    transform: rotate(2.5deg) translateY(-2%);
  }
}
@keyframes hop {
  0%,
  100% {
    transform: translateY(0) scale(1.04, 0.94);
  }
  45% {
    transform: translateY(-13%) scale(0.97, 1.05) rotate(-3deg);
  }
  70% {
    transform: translateY(-6%) rotate(2deg);
  }
}
@keyframes hopBig {
  0%,
  100% {
    transform: translateY(0) scale(1.06, 0.92);
  }
  45% {
    transform: translateY(-20%) scale(0.96, 1.08) rotate(-5deg);
  }
  70% {
    transform: translateY(-9%) rotate(4deg);
  }
}
@keyframes hopShadow {
  0%,
  100% {
    transform: scaleX(1.05);
    opacity: 1;
  }
  45% {
    transform: scaleX(0.6);
    opacity: 0.45;
  }
}
@keyframes droop {
  0%,
  100% {
    transform: translateY(2%) scale(1, 0.97);
  }
  50% {
    transform: translateY(4%) scale(1.01, 0.95);
  }
}
@keyframes blink {
  0%,
  92%,
  100% {
    transform: scaleY(1);
  }
  95% {
    transform: scaleY(0.08);
  }
}

/* --- Sparkles ------------------------------------------------------------- */

.sparkles {
  position: absolute;
  inset: 0;
}

.sparkles i {
  position: absolute;
  width: 11%;
  height: 11%;
  background: var(--accent-gold);
  clip-path: polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%);
  opacity: 0;
  animation: twinkle 1.3s ease-in-out infinite;
}
.sparkles i:nth-child(1) {
  top: 6%;
  left: 8%;
}
.sparkles i:nth-child(2) {
  top: 14%;
  right: 6%;
  animation-delay: 0.3s;
}
.sparkles i:nth-child(3) {
  top: 46%;
  left: -2%;
  animation-delay: 0.6s;
}
.sparkles i:nth-child(4) {
  top: 40%;
  right: -2%;
  animation-delay: 0.9s;
}

@keyframes twinkle {
  0%,
  100% {
    opacity: 0;
    transform: scale(0.4) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1) rotate(45deg);
  }
}

/* A decorative loop must never run for someone who asked for no motion. */
@media (prefers-reduced-motion: reduce) {
  .root *,
  .root {
    animation: none !important;
  }
}
